/**
 * User Dashboard Page
 * Home account overview:
 * - Account card: home address (tenant), token/parking API settings
 * - Members card: everyone in the household (owner + added members),
 *   each with their own vehicle and parking schedule
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Modal,
  Form,
  Alert,
} from 'react-bootstrap';
import {
  updateUser,
  registerParkingManually,
  getMembers,
  createMember,
  updateMember,
  deleteMember,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isValidTimeFormat, formatTime12Hour } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ResponsiveTable from '../components/ResponsiveTable';
import UserForm from '../components/UserForm';

const MemberModal = ({ show, onHide, member, onSave, error }) => {
  const isEdit = !!member?.id;
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setData({
      name: member?.name || '',
      license_plate: member?.license_plate || '',
      phone_number: member?.phone_number || '',
      auto_register: member?.auto_register !== undefined ? member.auto_register : true,
      pref_time: member?.pref_time || '19:00',
    });
    setErrors({});
  }, [member, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setData({ ...data, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = () => {
    const errs = {};
    if (!data.name?.trim()) {
      errs.name = 'Name is required';
    }
    if (!data.phone_number?.trim()) {
      errs.phone_number = 'Phone number is required';
    }
    if (!isValidTimeFormat(data.pref_time)) {
      errs.pref_time = 'Invalid time format (HH:MM)';
    }
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      onSave(data, member?.id);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? `Edit Member: ${member?.name}` : 'Add Household Member'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={data.name || ''}
              onChange={handleChange}
              isInvalid={!!errors.name}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
          </Form.Group>
          <Row>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>License Plate</Form.Label>
                <Form.Control
                  type="text"
                  name="license_plate"
                  value={data.license_plate || ''}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone (permit SMS) *</Form.Label>
                <Form.Control
                  type="text"
                  name="phone_number"
                  value={data.phone_number || ''}
                  onChange={handleChange}
                  isInvalid={!!errors.phone_number}
                />
                <Form.Control.Feedback type="invalid">{errors.phone_number}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Form.Group className="mb-3">
                <Form.Label>Auto-register Time (HH:MM)</Form.Label>
                <Form.Control
                  type="text"
                  name="pref_time"
                  value={data.pref_time || ''}
                  onChange={handleChange}
                  isInvalid={!!errors.pref_time}
                />
                <Form.Control.Feedback type="invalid">{errors.pref_time}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          {isValidTimeFormat(data.pref_time) && (
            <Form.Text className="text-muted d-block mb-3">
              <i className="bi bi-clock me-1"></i>
              Daily at {formatTime12Hour(data.pref_time)}
            </Form.Text>
          )}
          <Form.Check
            type="switch"
            name="auto_register"
            id="member-auto-register"
            label="Auto-register Parking"
            checked={data.auto_register || false}
            onChange={handleChange}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit}>{isEdit ? 'Save Changes' : 'Add Member'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

const UserDashboard = () => {
  const { user, refresh } = useAuth();
  const [members, setMembers] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const [memberModal, setMemberModal] = useState({ show: false, member: null, error: '' });
  const [showResultModal, setShowResultModal] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [registerTarget, setRegisterTarget] = useState(null);
  const [registrationHours, setRegistrationHours] = useState('8');
  const [paramValues, setParamValues] = useState({});
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await getMembers();
      setMembers(data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        tenant: user.tenant || '',
        name: user.name || '',
        license_plate: user.license_plate || '',
        phone_number: user.phone_number || '',
        auto_register: user.auto_register,
        pref_time: user.pref_time || '19:00',
      });
      fetchMembers();
    }
  }, [user, fetchMembers]);

  if (!user) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  // Everyone in the household: the owner (account row) + added members
  const household = [
    {
      id: user.id,
      name: user.name,
      license_plate: user.license_plate,
      phone_number: user.phone_number,
      auto_register: user.auto_register,
      pref_time: user.pref_time || '19:00',
      isOwner: true,
    },
    ...members.map((m) => ({ ...m, isOwner: false })),
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.tenant?.trim()) {
      errors.tenant = 'Home address is required';
    }
    if (!formData.name?.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.phone_number?.trim()) {
      errors.phone_number = 'Phone number is required';
    }
    if (!isValidTimeFormat(formData.pref_time)) {
      errors.pref_time = 'Invalid time format (HH:MM)';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAccount = async () => {
    if (!validateForm()) return;
    try {
      const payload = { ...formData };
      await updateUser(user.id, payload);
      await refresh();
      setShowEditModal(false);
      setSuccess('Account settings updated');
    } catch (err) {
      setFormErrors({ submit: err.response?.data?.error || err.message });
    }
  };

  const handleSaveMember = async (data, memberId) => {
    try {
      if (memberId) {
        await updateMember(memberId, data);
        setSuccess(`Member ${data.name} updated`);
      } else {
        await createMember(data);
        setSuccess(`Member ${data.name} added`);
      }
      setMemberModal({ show: false, member: null, error: '' });
      fetchMembers();
    } catch (err) {
      setMemberModal((prev) => ({ ...prev, error: err.response?.data?.error || err.message }));
    }
  };

  const handleDeleteMember = async (member) => {
    if (window.confirm(`Delete member ${member.name}?`)) {
      try {
        await deleteMember(member.id);
        setSuccess(`Member ${member.name} deleted`);
        fetchMembers();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    }
  };

  const handleRegisterNow = (person) => {
    if (!person.license_plate || !person.phone_number) {
      setError(
        `Cannot register parking: ${person.name} is missing a license plate or phone number. ` +
        `Click Edit to add ${!person.license_plate && !person.phone_number ? 'them' : 'it'}.`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setRegisterTarget(person);
    setRegistrationHours('8');
    // Pre-fill "changing" params with their configured defaults
    const changing = (user.parking_params || []).filter((p) => p && p.changing);
    setParamValues(Object.fromEntries(changing.map((p) => [p.key, p.value || ''])));
    setShowRegisterModal(true);
  };

  const changingParams = (user.parking_params || []).filter((p) => p && p.changing);

  const handleSubmitRegistration = async () => {
    const hours = parseInt(registrationHours, 10);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      setError('Please enter a valid number of hours (1-24)');
      return;
    }
    // Validate changing params
    for (const p of changingParams) {
      if (!String(paramValues[p.key] ?? '').trim()) {
        setError(`Please enter a value for "${p.key}"`);
        return;
      }
    }

    setShowRegisterModal(false);
    setRegistering(true);
    setError(null);
    try {
      const result = await registerParkingManually(registerTarget.id, hours, paramValues);
      setRegistrationResult({ ...result, personName: registerTarget.name });
      setShowResultModal(true);
    } catch (err) {
      setError(`Failed to register parking: ${err.response?.data?.error || err.message}`);
    } finally {
      setRegistering(false);
    }
  };

  const renderTime = (person) => (
    <>
      Daily at {person.pref_time}
      <br />
      <small className="text-muted">
        <i className="bi bi-clock me-1"></i>
        {formatTime12Hour(person.pref_time)}
      </small>
    </>
  );

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>My Parking</h2>
          <p className="text-muted mb-0">
            <i className="bi bi-house-door me-1"></i>{user.tenant}
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Household Members */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <span>Household Members</span>
                <Button variant="primary" size="sm" onClick={() => setMemberModal({ show: true, member: null, error: '' })}>
                  <i className="bi bi-person-plus me-1"></i> Add Member
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <ResponsiveTable
                data={household}
                columns={[
                  {
                    key: 'name',
                    label: 'Member',
                    render: (value, person) => (
                      <>
                        <strong>{person.name}</strong>
                        {person.isOwner && (
                          <Badge bg="info" className="ms-2">Owner</Badge>
                        )}
                        {(!person.license_plate || !person.phone_number) && (
                          <Badge bg="warning" text="dark" className="ms-2">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Missing {[!person.license_plate && 'plate', !person.phone_number && 'phone'].filter(Boolean).join(' & ')}
                          </Badge>
                        )}
                      </>
                    ),
                  },
                  {
                    key: 'license_plate',
                    label: 'License Plate',
                    render: (value, person) => person.license_plate || <span className="text-muted">N/A</span>,
                  },
                  {
                    key: 'phone_number',
                    label: 'Phone',
                    render: (value, person) => person.phone_number || <span className="text-muted">N/A</span>,
                  },
                  {
                    key: 'register_time',
                    label: 'Auto-register',
                    render: (value, person) => renderTime(person),
                  },
                  {
                    key: 'auto_register',
                    label: 'Auto Register',
                    render: (value, person) => (
                      <Badge bg={person.auto_register ? 'success' : 'secondary'}>
                        {person.auto_register ? 'ON' : 'OFF'}
                      </Badge>
                    ),
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (value, person) => (
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleRegisterNow(person)}
                          disabled={registering}
                        >
                          <i className="bi bi-p-square me-1"></i> Register Now
                        </Button>
                        {person.isOwner ? (
                          <Button variant="outline-primary" size="sm" onClick={() => setShowEditModal(true)}>
                            <i className="bi bi-pencil me-1"></i> Edit
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setMemberModal({ show: true, member: person, error: '' })}
                            >
                              <i className="bi bi-pencil me-1"></i> Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteMember(person)}>
                              <i className="bi bi-trash me-1"></i> Delete
                            </Button>
                          </>
                        )}
                      </div>
                    ),
                  },
                ]}
                renderRow={(person) => (
                  <>
                    <div className="table-card-primary">
                      {person.name}
                      {person.isOwner && <Badge bg="info" className="ms-2">Owner</Badge>}
                      {(!person.license_plate || !person.phone_number) && (
                        <Badge bg="warning" text="dark" className="ms-2">
                          Missing {[!person.license_plate && 'plate', !person.phone_number && 'phone'].filter(Boolean).join(' & ')}
                        </Badge>
                      )}
                    </div>
                    <div className="table-card-divider"></div>
                    <div className="table-card-field">
                      <strong>License Plate:</strong> {person.license_plate || 'N/A'}
                    </div>
                    <div className="table-card-field">
                      <strong>Phone:</strong> {person.phone_number || 'N/A'}
                    </div>
                    <div className="table-card-field">
                      <strong>Auto-register:</strong> daily at {person.pref_time}
                    </div>
                    <div className="table-card-field">
                      <strong>Auto Register:</strong>{' '}
                      <Badge bg={person.auto_register ? 'success' : 'secondary'}>
                        {person.auto_register ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                    <div className="table-card-divider"></div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleRegisterNow(person)}
                        disabled={registering}
                        style={{ flex: 1, minWidth: '120px', fontSize: '1.05rem', padding: '0.9rem 0.75rem' }}
                      >
                        <i className="bi bi-p-square me-1"></i> Register
                      </Button>
                      {person.isOwner ? (
                        <Button variant="outline-primary" size="sm" onClick={() => setShowEditModal(true)}
                          style={{ flex: 1, minWidth: '100px', fontSize: '1.05rem', padding: '0.9rem 0.75rem' }}>
                          Edit
                        </Button>
                      ) : (
                        <>
                          <Button variant="outline-primary" size="sm"
                            onClick={() => setMemberModal({ show: true, member: person, error: '' })}
                            style={{ flex: 1, minWidth: '100px', fontSize: '1.05rem', padding: '0.9rem 0.75rem' }}>
                            Edit
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteMember(person)}
                            style={{ flex: 1, minWidth: '100px', fontSize: '1.05rem', padding: '0.9rem 0.75rem' }}>
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </>
                )}
                emptyState={<EmptyState icon="person" message="No members yet" />}
                hover={true}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Account Settings Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Account Holder Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formErrors.submit && <Alert variant="danger">{formErrors.submit}</Alert>}
          <UserForm
            formData={formData}
            formErrors={formErrors}
            onChange={handleInputChange}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveAccount}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      {/* Member Add/Edit Modal */}
      <MemberModal
        show={memberModal.show}
        member={memberModal.member}
        error={memberModal.error}
        onHide={() => setMemberModal({ show: false, member: null, error: '' })}
        onSave={handleSaveMember}
      />

      {/* Registration Result Modal */}
      <Modal show={showResultModal} onHide={() => setShowResultModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {registrationResult?.success ? (
              <><i className="bi bi-check-circle-fill text-success me-2"></i> Registration Successful</>
            ) : (
              <><i className="bi bi-x-circle-fill text-danger me-2"></i> Registration Failed</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {registrationResult && (
            <>
              <Alert variant={registrationResult.success ? 'success' : 'danger'}>
                {registrationResult.message}
              </Alert>
              <ul>
                <li><strong>Member:</strong> {registrationResult.personName}</li>
                {registrationResult.user?.licensePlate && (
                  <li><strong>License Plate:</strong> {registrationResult.user.licensePlate}</li>
                )}
                {registrationResult.duration && (
                  <li><strong>Duration:</strong> {registrationResult.duration} hours</li>
                )}
                <li><strong>Status:</strong> HTTP {registrationResult.status} - {registrationResult.statusText}</li>
              </ul>
              {registrationResult.error && (
                <Alert variant="warning" className="mt-3">
                  <strong>Error:</strong> {registrationResult.error}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowResultModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Register Parking Modal */}
      <Modal show={showRegisterModal} onHide={() => setShowRegisterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-p-square me-2"></i>
            Register Parking{registerTarget ? `: ${registerTarget.name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info small mb-3">
            <i className="bi bi-info-circle-fill me-2"></i>
            Manual one-time registration. Members with Auto Register ON are
            registered automatically every day during their window.
          </div>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Duration (hours) *</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="24"
                value={registrationHours}
                onChange={(e) => setRegistrationHours(e.target.value)}
                autoFocus
              />
              <Form.Text className="text-muted">Enter a value between 1 and 24 hours</Form.Text>
            </Form.Group>

            {changingParams.map((p, i) => (
              <Form.Group className="mb-3" key={p.key}>
                <Form.Label>{p.key} *</Form.Label>
                <Form.Control
                  type="text"
                  value={paramValues[p.key] ?? ''}
                  onChange={(e) => setParamValues({ ...paramValues, [p.key]: e.target.value })}
                  autoFocus={i === 0 && registrationHours}
                />
                <Form.Text className="text-muted">Asked each time — set by your household in Settings</Form.Text>
              </Form.Group>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRegisterModal(false)}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleSubmitRegistration}
            disabled={!registrationHours || parseInt(registrationHours, 10) <= 0 || parseInt(registrationHours, 10) > 24}
          >
            <i className="bi bi-p-square me-1"></i>
            Register Parking
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserDashboard;
